import {
  AuthenticatedRoute,
  createClientLogger,
  getFirebaseAuth,
  getManagementServiceUrl,
  useAuth,
  useUserSignIn,
} from '@order-up/client/vite';
import { BrowserRouter as Router } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { ClientProviders } from './components/ClientProviders';
import { AppShellError } from './components/AppShellError';
import { OnboardingFlow } from './components/OnboardingFlow';
import { OnboardingLoadingShell } from './components/OnboardingLoadingShell';
import { SignOutButton } from './components/ui/SignOutButton';
import { useOnboardingApplication } from './lib/hooks';
import { markPhase } from './lib/firstPaint';

// First-paint phase: the onboarding bundle has parsed and the React tree
// is about to mount. Anything before this is browser parse + Vite HMR
// connection, both of which we can read from the browser's own
// `navigationStart` entry.
markPhase('app-shell-mount');

/**
 * Renders the onboarding UI once the user is authenticated.
 *
 * `AuthenticatedRoute` (wrapped around this in the `App` tree below) guarantees
 * we reach this component with a valid Firebase user AND a valid server session:
 *   - Loading states → handled by AuthenticatedRoute's OnboardingLoadingShell
 *   - Expired / missing session → AuthGuard inside AuthenticatedRoute redirects
 *     to the auth service with a `returnUrl`, and TokenHandler picks up the
 *     `?token=` query param on the way back.
 * So the only manual handling we need here is: read the user, render onboarding.
 */
function AppContent() {
  const { data } = useAuth();
  const user = data?.authUser;

  // Guaranteed by AuthenticatedRoute — but keep a defensive branch so TS is happy
  // and we don't render undefined-user UI if the guard regresses. Reuse
  // OnboardingLoadingShell so this frame matches what TokenHandler / AuthGuard
  // render and the user doesn't see a different-styled spinner flash through.
  if (!user) {
    return <OnboardingLoadingShell />;
  }

  // Onboarding data is served by the API (apps/api/.../routes/onboarding.ts),
  // not by a browser-side Supabase client.
  return <OnboardingApp user={user} />;
}

function OnboardingApp({ user }: { user: NonNullable<ReturnType<typeof useAuth>['data']>['authUser'] }) {
  const { application, isLoading, isError, error } = useOnboardingApplication(user?.uid);

  // First-paint phase: AuthGuard has cleared us into AppContent and our
  // useAuth hook has the user. Anything before this is the auth-handoff
  // chain (TokenHandler + AuthGuard).
  useEffect(() => {
    markPhase('auth-guard-pass');
  }, []);

  // First-paint phase: the application row is populated and the form
  // can render. The delta from `auth-guard-pass` to here is the
  // `/api/v1/onboarding/me` round-trip latency.
  useEffect(() => {
    if (application) {
      markPhase('application-loaded');
    }
  }, [application]);

  if (isError && error) {
    return (
      <AppShellError
        title="Failed to load application"
        description={
          <p>{typeof error === 'string' ? error : (error as Error).message}</p>
        }
        onRetry={() => window.location.reload()}
      />
    );
  }

  // While the initial fetch is in flight, render the real OnboardingFlow
  // chrome (brand, step rail, header, nav footer) with an in-place skeleton
  // for the step body. Mirrors AdminLoadingFallback / ManagementLoadingFallback.
  if (isLoading || !application) {
    return <OnboardingFlow />;
  }

  // application is already IOnboardingApplication (unwrapped by useOnboardingApplication)

  // Provisioning succeeded — bounce the user to the management app. The
  // synchronous-submit pipeline (W6) sets status to `provisioned` inside the
  // submit response; `approved` is a brief intermediate state during the
  // approve→provision chain and is treated identically here. The auth app
  // also lands the user on management via `<ManagementRedirect />` on next
  // sign-in, so the redirect from this page is best-effort within the
  // current tab session.
  if (application.status === 'provisioned' || application.status === 'approved') {
    return <AccountCreatedRedirect />;
  }

  // Stuck in `submitted` / `pending_review` — error state.
  // With synchronous submit there is no legitimate "waiting for review"
  // window. Reaching this branch means either: (a) a partial-failure
  // mid-provision left the row in `submitted` (recoverable via the admin
  // approve endpoint), or (b) a legacy row from before W6. Either way the
  // user can't move forward without ops intervention.
  if (application.status === 'submitted' || application.status === 'pending_review') {
    return (
      <AppShellError
        title="We hit a problem creating your account"
        description={
          <>
            <p>
              Your submission has been recorded but account provisioning did not
              complete. Please contact support and reference application ID{' '}
              <span className="font-mono text-zinc-300">{application.id}</span>.
            </p>
            <div className="pt-4">
              <SignOutButton variant="inline" />
            </div>
          </>
        }
      />
    );
  }

  // Show declined state if declined
  if (application.status === 'declined') {
    return (
      <div className="min-h-svh flex items-center justify-center bg-zinc-950">
        <div className="text-center space-y-6 max-w-md px-6">
          <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mx-auto">
            <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-white">Application Declined</h2>
            <p className="text-zinc-400">
              Unfortunately, we're unable to approve your application at this time.
            </p>
            {application.copilotResponse?.declineReason && (
              <p className="text-sm text-zinc-400 mt-4">
                Reason: {application.copilotResponse.declineReason}
              </p>
            )}
          </div>
          <SignOutButton variant="inline" />
        </div>
      </div>
    );
  }

  // Show onboarding flow for draft applications
  return <OnboardingFlow application={application} />;
}

function App() {
  return (
    <Router>
      <ClientProviders>
        {/**
         * AuthenticatedRoute handles the full auth handshake:
         *   1. TokenHandler consumes a `?token=` query param if the auth
         *      service just redirected back, exchanging it for a session.
         *   2. AuthGuard checks Firebase + server session; if neither is
         *      valid, redirects to the auth service with the current URL
         *      as `returnUrl` so the user lands back here after signing in.
         *   3. Only after both pass does it render children, at which point
         *      `useAuth()` inside AppContent is guaranteed to return a user.
         *
         * Do not re-roll manual `useAuth` + `if (!user)` checks here — that's
         * what was causing the infinite spinner before.
         */}
        <AuthenticatedRoute loadingComponent={OnboardingLoadingShell}>
          <AppContent />
        </AuthenticatedRoute>
      </ClientProviders>
    </Router>
  );
}

const redirectLogger = createClientLogger('AccountCreatedRedirect');

/**
 * Renders the "Account created — redirecting" success card and bounces the
 * user to the management app. The auth app's `<ManagementRedirect />` is
 * the canonical post-provisioning landing path; this component covers the
 * narrow case where the user remains on the onboarding origin after the
 * synchronous-submit response (e.g., the onboarding-form submit handler
 * just resolved and the SPA hasn't navigated yet).
 *
 * Before bouncing, it RE-MINTS the server session cookie. Provisioning grants
 * the tenant `owner` assignment and syncs the user's Firebase claims
 * synchronously (userService.grantTenantAssignment → claimsService.syncClaims),
 * but it does NOT revoke the session cookie minted during signup — which
 * predates the grant and therefore carries no tenant role. The management app
 * shares the `.letsorderup.com` cookie, so without this it would inherit that
 * stale, grant-less cookie and every Books API call would 403
 * (BOOKS_ACCESS_DENIED). Force-refreshing the ID token pulls the freshly-minted
 * `owner` claim; re-posting it to /auth/signin reissues the cookie WITH the
 * grant. Best-effort: any failure still redirects (no worse than before; a
 * sign-out/in recovers).
 */
function AccountCreatedRedirect() {
  const signIn = useUserSignIn();
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return; // guard StrictMode double-invoke
    startedRef.current = true;

    const handoff = () => {
      window.location.href = getManagementServiceUrl();
    };

    void (async () => {
      try {
        const user = getFirebaseAuth().currentUser;
        if (user) {
          const idToken = await user.getIdToken(true); // force refresh → new claims
          await signIn.mutateAsync({ idToken, rememberMe: true });
        }
      } catch (err) {
        redirectLogger.warn(
          'Session re-mint before management handoff failed; redirecting with existing cookie',
          { error: err instanceof Error ? err.message : String(err) },
        );
      } finally {
        handoff();
      }
    })();
  }, [signIn]);

  return (
    <div className="min-h-svh flex items-center justify-center bg-zinc-950">
      <div className="text-center space-y-6 max-w-md px-6">
        <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
          <svg
            className="w-10 h-10 text-emerald-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <div className="space-y-2">
          <h2 className="text-xl font-semibold text-white">Account created</h2>
          <p className="text-zinc-400">Welcome to OrderUp. Taking you to your dashboard…</p>
        </div>
      </div>
    </div>
  );
}

export default App;
